import * as Sentry from "@sentry/react";
import { createPortfolio, getPortfolios } from "@/pages/PortfolioBuilder/api";
import { createTagsWatchlist, getTagsWatchlists } from "@/shared/api/pref-storage/tags-watchlists";
import {
  getQueries,
  postQuery,
} from "@/widget-components/newsquawk-headlines/api/headline-query";
import { transformHeadlineQueries } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import type { Portfolio } from "@/types/Portfolio";
import { emptyNotifications } from "@/types/notifications";
import { makeNameClaimer } from "./claimName";
import { backfillWatchlistTags } from "@/shared/utils/backfillWatchlistTags";
import { getWidgetById, type Widget, type WidgetFilterFeature } from "@/utils/widgets";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { buildLayoutExportData } from "../templates/packLayout";
import { applyLayoutData } from "../templates/applyTemplate";
import type { TemplateWidgetSpec } from "../templates/types";
import type { ProvisioningPlan } from "./types";

export type ApplyPlanResult = {
  createdPortfolios: number;
  createdTagWatchlists: number;
  createdQueries: number;
  tabTitle: string | null;
  /** Plan ref → real resource id, including resolved lib:/libq: refs. Lets a
   *  caller keep working with what was just created (e.g. handing a filter to
   *  the AND/OR builder with its watchlists already attached). */
  refToId: Record<string, number>;
  /**
   * The Newsquawk filters behind resolved libq: refs, keyed by ref.
   *
   * A library filter is a whole recipe — tags, keyword, lists and the AND/OR
   * between them — so its id alone is no use to a caller that wants to build
   * on it. They were already fetched to resolve the ids; handing the records
   * back saves that caller a second fetch.
   */
  libraryQueries: Record<string, CategoryQuery>;
};

/**
 * Executes a ProvisioningPlan with the user's own token, in dependency order:
 * portfolios → tag watchlists → headline queries (building a ref → id map),
 * then the dashboard tab through the same buildLayoutExportData →
 * applyLayoutData pipeline the starter templates use. The backend never
 * writes anything — this is the only write path.
 *
 * Partial-failure model: writes are applied in order and reported per step;
 * a failure mid-way leaves earlier resources in place (visible in the
 * builders, deletable) — acceptable for v1, no rollback machinery.
 */
type RefKind = "ticker" | "keyword" | "tag" | "query";

/** Filter-panel section for a referenced resource, by kind. */
const FILTER_SECTION: Record<RefKind, string> = {
  ticker: "ticker-watchlists-filter",
  keyword: "keyword-watchlists-filter",
  tag: "tag-watchlists-filter",
  query: "category-filter",
};

const HIGHLIGHT_SECTION: Partial<Record<RefKind, string>> = {
  ticker: "ticker-watchlists-highlight",
  keyword: "keyword-watchlists-highlight",
};

/**
 * The filterFeature a widget must declare to be able to use each kind of ref.
 *
 * Mirrors the sections WidgetFilterPanel actually renders — that component
 * builds its menus from `filterFeatures`, so a section the widget does not
 * declare has no UI, no reader, and no effect.
 */
const REQUIRED_FEATURE: Record<RefKind, WidgetFilterFeature> = {
  ticker: "ticker-watchlists",
  keyword: "keyword-watchlists",
  tag: "tag-watchlists",
  query: "advanced-filters",
};

/**
 * Can this widget actually act on a ref of this kind?
 *
 * Only two of the thirty-one widgets declare "tag-watchlists" or
 * "asset-classes" — NewsquawkHeadlines and ResearchHeadlines. Everything else,
 * including the core feeds (FinX, TruthSocial, StocksNews, StocksRatings),
 * takes ticker and keyword watchlists only.
 *
 * The plan prompt states this, and a plan that obeys it needs no help here.
 * The check exists because obeying it is the model's job and honouring it is
 * ours: without it a tag ref on StocksNews wrote a `tag-watchlists-filter` key
 * into that widget's persisted filters, where nothing reads it. The feed then
 * showed its unfiltered default — which looks like a filter that does not work
 * rather than one that was never applicable.
 *
 * A widget with no `filterFeatures` at all filters on nothing (MarketSnapshot,
 * the calendars, the TV widgets), so everything is refused.
 */
function widgetAccepts(widgetId: string, kind: RefKind): boolean {
  const features = getWidgetById(widgetId)?.filterFeatures;
  return !!features?.includes(REQUIRED_FEATURE[kind]);
}

export async function applyPlan(
  plan: ProvisioningPlan,
  options: {
    hasAccessToWidget: (widget: Widget) => boolean;
    /**
     * Watchlists to highlight on the plan's primary headlines instance —
     * the user's own portfolio from step 1, so their names stand out in the
     * wire the concierge built for them.
     */
    highlightWatchlistIds?: number[];
    /**
     * Progress for the caller's button. Applying a multi-list plan is several
     * seconds of tag lookups and writes, and a button that only says "Saving…"
     * for all of it reads as hung.
     */
    onProgress?: (step: string) => void;
    /**
     * Fires when the created watchlists' tags have landed, some seconds after
     * `applyPlan` itself resolves. Callers use it to refetch, so the tags show
     * up without a reload.
     */
    onTagsBackfilled?: () => void;
  }
): Promise<ApplyPlanResult> {
  const refToId = new Map<string, number>();

  // The plan references resources by bare ref; the portal's filter sections
  // are derived from each ref's resource type.
  const refKind = new Map<string, RefKind>();
  for (const p of plan.portfolios) {
    refKind.set(p.ref, p.tickers.length > 0 ? "ticker" : "keyword");
  }
  for (const t of plan.tags_watchlists) refKind.set(t.ref, "tag");
  for (const q of plan.headline_queries) refKind.set(q.ref, "query");

  // "lib:<name>" / "libq:<name>" refs point at NEWSQUAWK prebuilt watchlists
  // and filters — nothing is created for them; they resolve by NAME against
  // the live library (ids differ per environment, names are the stable key).
  // Unresolved names (library drift) drop silently; the rest still applies.
  const libRefs = new Set<string>();
  const libQueryRefs = new Set<string>();
  const noteRef = (ref: string) => {
    if (ref.startsWith("libq:")) libQueryRefs.add(ref);
    else if (ref.startsWith("lib:")) libRefs.add(ref);
  };
  if (plan.dashboard_tab) {
    for (const widget of plan.dashboard_tab.widgets) {
      for (const ref of [...widget.highlight_refs, ...widget.filter_refs]) {
        noteRef(ref);
      }
    }
  }
  // Filters reference library lists too — a query composed as "tags [Energy]
  // OR watchlists [lib:OPEC & Oil Supply]" loses half its reach if these
  // aren't resolved alongside the widget refs.
  for (const query of plan.headline_queries) {
    for (const ref of [...query.portfolio_refs, ...query.tags_watchlist_refs]) {
      noteRef(ref);
    }
  }
  // A reuse-only plan creates nothing, so these are the only refs it has.
  // Resolving them is what lets the caller act on "these lists already cover
  // it" — without ids there is nothing to hand to the combine route.
  for (const ref of plan.reused_refs ?? []) noteRef(ref);

  let existing: Portfolio[] = [];
  if (plan.portfolios.length > 0 || libRefs.size > 0) {
    existing = await getPortfolios();
  }
  for (const ref of libRefs) {
    const name = ref.slice(4);
    const match = existing.find(
      (p) => p.owner_type === "NEWSQUAWK" && p.name === name
    );
    if (match) {
      refToId.set(ref, match.id);
      refKind.set(ref, match.tickers.length > 0 ? "ticker" : "keyword");
    }
  }

  const libraryQueries: Record<string, CategoryQuery> = {};
  if (libQueryRefs.size > 0) {
    const response = await getQueries();
    const queries = transformHeadlineQueries(
      response.data?.items ?? response.data ?? []
    );
    for (const ref of libQueryRefs) {
      const name = ref.slice(5);
      const match = queries.find(
        (q) => q.owner_type === "NEWSQUAWK" && q.queryName === name
      );
      if (match && typeof match.id === "number" && !Number.isNaN(match.id)) {
        refToId.set(ref, match.id);
        refKind.set(ref, "query");
        libraryQueries[ref] = match;
      }
    }
  }

  // Names the user already has, so a plan cannot silently shadow one of their
  // own lists either. Best-effort on purpose: if a lookup fails we still
  // de-duplicate within the plan rather than abandoning the apply, because a
  // failed GET is no reason to refuse to build what the user asked for.
  const creatingAnything =
    plan.portfolios.length > 0 ||
    plan.tags_watchlists.length > 0 ||
    plan.headline_queries.length > 0;

  const takenNames: string[] = [];
  if (creatingAnything) {
    if (existing.length === 0) {
      existing = await getPortfolios().catch(() => [] as Portfolio[]);
    }
    takenNames.push(...existing.map((p) => p.name));

    const [tagNames, queryNames] = await Promise.all([
      getTagsWatchlists()
        .then((r) => (r.data ?? []).map((w) => w.name))
        .catch(() => [] as string[]),
      getQueries()
        .then((r) => {
          const rows: { name?: string }[] =
            r.data?.items ?? r.data ?? [];
          return rows.map((q) => q.name ?? "").filter(Boolean);
        })
        .catch(() => [] as string[]),
    ]);
    takenNames.push(...tagNames, ...queryNames);
  }
  const claimName = makeNameClaimer(takenNames);

  // 1. Portfolios — the pref-storage API expects a client-allocated id
  //    (mirrors useWatchlistCreator's convention).
  let createdPortfolios = 0;
  const createdIds: number[] = [];
  if (plan.portfolios.length > 0) {
    let nextId = Math.max(0, ...existing.map((p: Portfolio) => p.id)) + 1;
    for (const portfolioPlan of plan.portfolios) {
      options.onProgress?.(`Creating ${portfolioPlan.name}…`);

      const portfolio: Portfolio = {
        id: nextId,
        name: claimName(portfolioPlan.name),
        tickers: portfolioPlan.tickers,
        keywords: portfolioPlan.keywords,
        // Filled in by the backfill below, not here: resolving a term is a
        // fan-out of tag lookups, and a twenty-ticker plan spent seconds on
        // them before writing anything. See backfillWatchlistTags.
        tags: [],
        color: "#FF0000",
        priority: 1,
        notifications: emptyNotifications(),
        customizations: { includeRelatedHeadlines: false },
        owner_type: "USER",
      };
      await createPortfolio(portfolio);
      refToId.set(portfolioPlan.ref, nextId);
      createdIds.push(nextId);
      nextId += 1;
      createdPortfolios += 1;
    }

    // Unawaited on purpose — the lists are saved and usable now, and match on
    // text until their tags land a moment later.
    backfillWatchlistTags(createdIds, options.onTagsBackfilled);
  }

  // 2. Tag watchlists — server allocates the id.
  let createdTagWatchlists = 0;
  for (const watchlistPlan of plan.tags_watchlists) {
    options.onProgress?.(`Creating ${watchlistPlan.name}…`);
    const response = await createTagsWatchlist({
      name: claimName(watchlistPlan.name),
      tags: watchlistPlan.tags,
    });
    const id = response.data?.id;
    if (typeof id === "number") refToId.set(watchlistPlan.ref, id);
    createdTagWatchlists += 1;
  }

  // 3. Headline queries — substitute refs into real ids.
  let createdQueries = 0;
  for (const queryPlan of plan.headline_queries) {
    options.onProgress?.(`Creating ${queryPlan.name}…`);
    const query: CategoryQuery = {
      id: undefined,
      queryName: claimName(queryPlan.name),
      categories: queryPlan.tags,
      searchKeyword: queryPlan.search_keyword || undefined,
      portfolioIds: queryPlan.portfolio_refs
        .map((ref) => refToId.get(ref))
        .filter((id): id is number => typeof id === "number"),
      tagsWatchlistIds: queryPlan.tags_watchlist_refs
        .map((ref) => refToId.get(ref))
        .filter((id): id is number => typeof id === "number"),
      keywordOp: queryPlan.keyword_op,
      filterMode: queryPlan.filter_op,
      keywordWatchlistOp: "OR",
      notifications: emptyNotifications(),
    };
    const response = await postQuery(query);
    const id = response.data?.id;
    if (typeof id === "number") refToId.set(queryPlan.ref, id);
    createdQueries += 1;
  }

  // 4. Dashboard tab via the shared template pipeline. The plan carries an
  //    ordered widget list only — geometry is packed client-side.
  let tabTitle: string | null = null;
  if (plan.dashboard_tab) {
    options.onProgress?.("Building your dashboard tab…");
    /**
     * Refs the plan placed on a widget that cannot act on them.
     *
     * Collected rather than thrown: the dashboard the user asked for is
     * already most of the way built and must not fail over one misplaced ref.
     * Reported once at the end, because a plan doing this is a prompt bug —
     * the model has the capability list and is told the rule — and silence is
     * what let it go unnoticed.
     */
    const droppedRefs: string[] = [];
    const usedSuffixes = new Map<string, number>();
    const specs: TemplateWidgetSpec[] = plan.dashboard_tab.widgets.map(
      (widgetPlan) => {
        // Unique pseudo-composite ids so duplicate widgets remap cleanly.
        const count = usedSuffixes.get(widgetPlan.widget_id) ?? 0;
        usedSuffixes.set(widgetPlan.widget_id, count + 1);
        const id =
          count === 0
            ? `${widgetPlan.widget_id}-a`
            : `${widgetPlan.widget_id}-${String.fromCharCode(97 + count)}`;

        const filter: Record<string, string[]> = {};
        const highlight: Record<string, string[]> = {};

        const addRef = (
          target: Record<string, string[]>,
          sections: Partial<Record<RefKind, string>>,
          ref: string
        ) => {
          const kind = refKind.get(ref);
          const id = refToId.get(ref);
          const section = kind && sections[kind];
          if (!section || id === undefined) return;
          // Refuse what the widget cannot read. Writing it would persist a
          // section key with no reader and leave the feed on its unfiltered
          // default — indistinguishable, on screen, from a filter that is
          // broken.
          if (!widgetAccepts(widgetPlan.widget_id, kind)) {
            droppedRefs.push(`${widgetPlan.widget_id}:${kind}:${ref}`);
            return;
          }
          (target[section] ??= []).push(String(id));
        };

        for (const ref of widgetPlan.highlight_refs) {
          addRef(highlight, HIGHLIGHT_SECTION, ref);
        }
        for (const ref of widgetPlan.filter_refs) {
          addRef(filter, FILTER_SECTION, ref);
        }
        if (widgetPlan.asset_class_tags.length > 0) {
          // Same rule for the asset-class row, which is its own feature.
          if (getWidgetById(widgetPlan.widget_id)?.filterFeatures?.includes(
            "asset-classes"
          )) {
            filter["basic-filter"] = widgetPlan.asset_class_tags;
          } else {
            droppedRefs.push(`${widgetPlan.widget_id}:asset-classes`);
          }
        }

        return {
          id,
          ...(Object.keys(filter).length > 0 && { filter }),
          ...(Object.keys(highlight).length > 0 && { highlight }),
        };
      }
    );

    if (droppedRefs.length > 0) {
      // Sentry rather than a toast: nothing the user can act on, and the plan
      // still applied. What it answers is "how often does the model put a tag
      // on a feed that has no tags", which is a question about the prompt.
      Sentry.captureMessage("concierge: plan refs dropped as unusable", {
        level: "warning",
        extra: { droppedRefs },
      });
    }

    const data = buildLayoutExportData(plan.dashboard_tab.title, specs);
    const result = applyLayoutData(data, {
      hasAccessToWidget: options.hasAccessToWidget,
      highlightWatchlistIds: options.highlightWatchlistIds,
      repackOnDrop: true,
    });
    tabTitle = result.tabTitle;
  }

  return {
    createdPortfolios,
    createdTagWatchlists,
    createdQueries,
    tabTitle,
    refToId: Object.fromEntries(refToId),
    libraryQueries,
  };
}
